import {
    ChevronRightIcon,
    CursorArrowRaysIcon,
    EnvelopeOpenIcon,
    PaperClipIcon,
} from "@heroicons/react/24/outline";
import {
    EmailEntityId,
    relativeTimeAgo,
    ReplyAction,
    SentMessageId,
    SentMessageType,
    UserId,
    UserMailboxMessageId,
} from "@sp-crm/core";
import { Button } from "components/layout/button";
import {
    AdvancedSearchEntityType,
    ConversationFeedBasicsFragment,
    SentMessageFragmentFragment,
} from "generated/graphql";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCurrentUserId, useFeature, useUsers } from "store/selectors/hooks";
import { getSanitizedMarkUpFromEmailBody } from "../../util/email";
import { handleEvent } from "../../util/user-events";
import { useUserMailboxes } from "./use-user-mailboxes";
import {
    buildReplyFromReceived,
    buildReplyFromSent,
    isCurrentUserEmail,
    ReplyState,
} from "./email-reply-utils";
import { MessageCompose } from "./message-compose";
import { MessageIndicatorCount } from "./message-indicator-count";
import { ReceivedMessage } from "./received-message-detail";
import { ReceivedMessageInlineDetail } from "./received-message-inline-detail";
import { SentMessageInlineDetail } from "./sent-message-inline-detail";
import {
    BouncedStatusMessage,
    DeliveredStatusMessage,
    NotDeliveredStatusMessage,
    SendingStatusMessage,
    SentStatusMessage,
} from "./status-message";
import {
    MessageBodyData,
    useReceivedMessageBody,
    useSentMessageBody,
} from "./use-message-body";

type EmailThreadType = NonNullable<
    ConversationFeedBasicsFragment["threads"][number]["emailThread"]
>;
type MailboxMessageType = EmailThreadType["mailboxMessages"][number];

interface ThreadMessage {
    id: string;
    type: "sent" | "received";
    timestamp: Date;
    senderName: string;
    bodyPreview: string;
    attachmentCount: number;
    openedCount: number;
    clickCount: number;
    originalSent?: SentMessageFragmentFragment;
    originalReceived?: MailboxMessageType;
}

interface UserLookup {
    [userId: string]: { name: string } | undefined;
}

function getSenderName(
    sent: SentMessageFragmentFragment,
    currentUserId: UserId | null,
    users: UserLookup,
): string {
    if (sent.userId === currentUserId) {
        return "You";
    }
    if (sent.userId && users[sent.userId]) {
        return users[sent.userId]?.name ?? "Unknown";
    }
    return "Unknown";
}

function getOpenedCount(sent: SentMessageFragmentFragment): number {
    return sent.events ? sent.events.filter(e => e.eventType === "opened").length : 0;
}

function getClickCount(sent: SentMessageFragmentFragment): number {
    return (
        sent.clickedLinkEvents?.reduce((acc, e) => acc + (e.eventsV2?.length || 0), 0) ||
        0
    );
}

function getReceivedSenderName(
    received: MailboxMessageType,
    currentUserEmails: string[],
): string {
    const fromEmail = received.from?.email;
    if (fromEmail && isCurrentUserEmail(fromEmail, currentUserEmails)) {
        return "You";
    }
    return (
        received.sender?.resolvedName ||
        received.sender?.name ||
        received.from?.resolvedName ||
        received.from?.name ||
        "Unknown"
    );
}

function buildThreadMessages(
    emailThread: EmailThreadType,
    currentUserId: UserId | null,
    users: UserLookup,
    currentUserEmails: string[],
): ThreadMessage[] {
    const messages: ThreadMessage[] = [];

    for (const sent of emailThread.sentMessages) {
        messages.push({
            id: sent.id,
            type: "sent",
            timestamp: new Date(sent.createdAt),
            senderName: getSenderName(sent, currentUserId, users),
            bodyPreview: sent.bodyPreview ?? sent.subject ?? "",
            attachmentCount: sent.attachments?.length ?? 0,
            openedCount: getOpenedCount(sent),
            clickCount: getClickCount(sent),
            originalSent: sent,
        });
    }

    for (const received of emailThread.mailboxMessages) {
        messages.push({
            id: received.id,
            type: "received",
            timestamp: new Date(received.receivedTimestamp),
            senderName: getReceivedSenderName(received, currentUserEmails),
            bodyPreview: received.bodyPreview,
            attachmentCount: received.attachmentCount,
            openedCount: 0,
            clickCount: 0,
            originalReceived: received,
        });
    }

    messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    return messages;
}

function getThreadSubject(allMessages: ThreadMessage[]): string {
    if (allMessages.length === 0) {
        return "(no subject)";
    }
    const oldest = allMessages[0];
    if (oldest.type === "sent" && oldest.originalSent) {
        return oldest.originalSent.subject || "(no subject)";
    }
    if (oldest.type === "received" && oldest.originalReceived) {
        return oldest.originalReceived.subject || "(no subject)";
    }
    return "(no subject)";
}

function formatQuotedDate(date: Date): string {
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

function escapeHtml(text: string): string {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function buildQuotedBody(senderName: string, timestamp: Date, bodyHtml: string): string {
    const dateStr = formatQuotedDate(timestamp);
    const safeName = escapeHtml(senderName);
    return [
        "<br/>",
        `<div>On ${dateStr}, ${safeName} wrote:</div>`,
        '<blockquote style="margin:0 0 0 .8ex;border-left:1px #ccc solid;padding-left:1ex">',
        bodyHtml,
        "</blockquote>",
    ].join("");
}

interface SentMessageStatusProps {
    status: string;
    latestEventStatus: string | null | undefined;
    messageType: SentMessageType;
}

const SentMessageStatusIndicator: React.FC<SentMessageStatusProps> = props => {
    const { status, latestEventStatus, messageType } = props;

    if (status === "warning") {
        return <BouncedStatusMessage mode="icon" />;
    }
    if (status === "error") {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (status === "success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (
        latestEventStatus === "fax-send-failure" ||
        latestEventStatus === "fax-delivery-failure"
    ) {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (latestEventStatus === "fax-delivery-success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (!latestEventStatus && messageType === "ClientIntakeFax") {
        return <SendingStatusMessage mode="icon" />;
    }
    if (latestEventStatus === "email-send-success") {
        return <SentStatusMessage mode="icon" />;
    }

    return null;
};

function toReceivedMessage(mailboxMessage: MailboxMessageType): ReceivedMessage {
    return {
        id: mailboxMessage.id,
        receivedTimestamp: mailboxMessage.receivedTimestamp,
        subject: mailboxMessage.subject,
        bodyPreview: mailboxMessage.bodyPreview,
        attachmentCount: mailboxMessage.attachmentCount,
        from: mailboxMessage.from,
        to: mailboxMessage.to,
        cc: mailboxMessage.cc,
        bcc: mailboxMessage.bcc,
    };
}

const ReplyIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg
        data-slot="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`h-3.5 w-3.5 ${className ?? ""}`}
        aria-hidden="true">
        <polyline points="9 17 4 12 9 7" />
        <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
    </svg>
);

const ReplyAllIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg
        data-slot="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`h-3.5 w-3.5 ${className ?? ""}`}
        aria-hidden="true">
        <polyline points="7 17 2 12 7 7" />
        <polyline points="12 17 7 12 12 7" />
        <path d="M22 18v-2a4 4 0 0 0-4-4H7" />
    </svg>
);

interface InlineReplyButtonsProps {
    message: ThreadMessage;
    onReply: (message: ThreadMessage, body: MessageBodyData) => void;
    onReplyAll: (message: ThreadMessage, body: MessageBodyData) => void;
    body: MessageBodyData;
}

const InlineReplyButtons: React.FC<InlineReplyButtonsProps> = props => {
    const { message, onReply, onReplyAll, body } = props;
    return (
        <div className="flex items-center gap-0 flex-shrink-0">
            <Button
                type="button"
                plain
                title="Reply"
                className="inline-flex items-center text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onReply(message, body);
                }}>
                <ReplyIcon />
            </Button>
            <Button
                type="button"
                plain
                title="Reply All"
                className="inline-flex items-center text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onReplyAll(message, body);
                }}>
                <ReplyAllIcon />
            </Button>
        </div>
    );
};

interface ReceivedMessageReplyButtonsProps {
    message: ThreadMessage;
    entityId: EmailEntityId | undefined;
    onReply: (message: ThreadMessage, body: MessageBodyData) => void;
    onReplyAll: (message: ThreadMessage, body: MessageBodyData) => void;
}

const ReceivedMessageReplyButtons: React.FC<ReceivedMessageReplyButtonsProps> = props => {
    const { message, entityId, onReply, onReplyAll } = props;
    const body = useReceivedMessageBody(entityId, message.id as UserMailboxMessageId);
    return (
        <InlineReplyButtons
            message={message}
            onReply={onReply}
            onReplyAll={onReplyAll}
            body={body}
        />
    );
};

interface SentMessageReplyButtonsProps {
    message: ThreadMessage;
    messageId: SentMessageId;
    onReply: (message: ThreadMessage, body: MessageBodyData) => void;
    onReplyAll: (message: ThreadMessage, body: MessageBodyData) => void;
}

const SentMessageReplyButtons: React.FC<SentMessageReplyButtonsProps> = props => {
    const { message, messageId, onReply, onReplyAll } = props;
    const body = useSentMessageBody(messageId);
    return (
        <InlineReplyButtons
            message={message}
            onReply={onReply}
            onReplyAll={onReplyAll}
            body={body}
        />
    );
};

interface EmailThreadComponentInlineProps {
    thread: ConversationFeedBasicsFragment["threads"][number];
    entityId?: EmailEntityId;
    entityType?: AdvancedSearchEntityType;
}

export const EmailThreadComponentInline: React.FC<
    EmailThreadComponentInlineProps
> = props => {
    const { thread, entityId, entityType } = props;
    const emailThread = thread.emailThread;
    const [expandedMessageIds, setExpandedMessageIds] = useState<Set<string>>(new Set());
    const [replyState, setReplyState] = useState<ReplyState | null>(null);

    const currentUserId = useCurrentUserId();
    const usersState = useUsers();
    const enhancedMode = useFeature("enhancedSentMessageView");
    const { activeMailbox } = useUserMailboxes();

    const canReply = !!activeMailbox && !!entityId && !!entityType;
    const currentUserEmail = activeMailbox?.email;
    const currentUserAccountEmail = currentUserId
        ? usersState.users[currentUserId]?.email
        : undefined;

    const currentUserEmails = useMemo(() => {
        const emails: string[] = [];
        if (currentUserEmail) emails.push(currentUserEmail);
        if (
            currentUserAccountEmail &&
            (!currentUserEmail ||
                currentUserAccountEmail.toLowerCase() !== currentUserEmail.toLowerCase())
        ) {
            emails.push(currentUserAccountEmail);
        }
        return emails;
    }, [currentUserEmail, currentUserAccountEmail]);

    const allMessages = useMemo(() => {
        if (!emailThread) return [];
        return buildThreadMessages(
            emailThread,
            currentUserId,
            usersState.users,
            currentUserEmails,
        );
    }, [emailThread, currentUserId, usersState.users, currentUserEmails]);

    const latestMessageId = allMessages[allMessages.length - 1]?.id;

    useEffect(() => {
        if (!latestMessageId) {
            return;
        }
        setExpandedMessageIds(prev => {
            if (prev.has(latestMessageId)) {
                return prev;
            }
            const next = new Set(prev);
            next.add(latestMessageId);
            return next;
        });
    }, [latestMessageId]);

    const toggleMessageExpanded = useCallback((messageId: string) => {
        setExpandedMessageIds(prev => {
            const next = new Set(prev);
            if (next.has(messageId)) {
                next.delete(messageId);
            } else {
                next.add(messageId);
            }
            return next;
        });
    }, []);

    if (!emailThread) {
        return null;
    }

    const subject = getThreadSubject(allMessages);

    const buildReplyForMessage = (
        message: ThreadMessage,
        replyType: ReplyAction,
        body: MessageBodyData,
    ): ReplyState | null => {
        const quotedSenderName =
            message.senderName === "You"
                ? (usersState.users[currentUserId!]?.name ?? "You")
                : message.senderName;

        let contentHtml: string;
        if (body?.html) {
            contentHtml = getSanitizedMarkUpFromEmailBody(body.html).__html;
        } else if (body?.plain) {
            contentHtml = escapeHtml(body.plain).replace(/\n/g, "<br/>");
        } else {
            contentHtml = escapeHtml(message.bodyPreview).replace(/\n/g, "<br/>");
        }

        const quotedBody = buildQuotedBody(
            quotedSenderName,
            message.timestamp,
            contentHtml,
        );

        if (message.type === "received" && message.originalReceived) {
            const base = buildReplyFromReceived(
                message.originalReceived,
                subject,
                replyType,
                currentUserEmails,
            );
            return { ...base, initialBody: quotedBody };
        }
        if (message.type === "sent" && message.originalSent) {
            const base = buildReplyFromSent(
                message.originalSent,
                subject,
                replyType,
                currentUserEmails,
            );
            return { ...base, initialBody: quotedBody };
        }
        return null;
    };

    const handleReply = (message: ThreadMessage, body: MessageBodyData) => {
        const state = buildReplyForMessage(message, ReplyAction.Reply, body);
        if (state) {
            handleEvent("message-reply");
            setReplyState(state);
        }
    };

    const handleReplyAll = (message: ThreadMessage, body: MessageBodyData) => {
        const state = buildReplyForMessage(message, ReplyAction.ReplyAll, body);
        if (state) {
            handleEvent("message-reply-all");
            setReplyState(state);
        }
    };

    const canCollapse = allMessages.length > 1;

    return (
        <li key={thread.id}>
            <div className="bg-white overflow-hidden">
                <div>
                    {allMessages.map(message => {
                        const isExpanded = expandedMessageIds.has(message.id);
                        const collapseHandler = canCollapse
                            ? () => toggleMessageExpanded(message.id)
                            : undefined;

                        if (isExpanded) {
                            if (message.type === "sent" && message.originalSent) {
                                return (
                                    <SentMessageInlineDetail
                                        key={message.id}
                                        messageId={
                                            message.originalSent.id as SentMessageId
                                        }
                                        attachmentCount={message.attachmentCount}
                                        status={message.originalSent.status}
                                        latestEventStatus={
                                            message.originalSent.latestEventStatus
                                        }
                                        messageType={
                                            message.originalSent
                                                .messageType as SentMessageType
                                        }
                                        onCollapse={collapseHandler}
                                        openedCount={message.openedCount}
                                        clickCount={message.clickCount}
                                        senderName={message.senderName}
                                        headerActions={
                                            canReply ? (
                                                <SentMessageReplyButtons
                                                    message={message}
                                                    messageId={
                                                        message.originalSent
                                                            .id as SentMessageId
                                                    }
                                                    onReply={handleReply}
                                                    onReplyAll={handleReplyAll}
                                                />
                                            ) : undefined
                                        }
                                    />
                                );
                            }
                            if (message.type === "received" && message.originalReceived) {
                                return (
                                    <ReceivedMessageInlineDetail
                                        key={message.id}
                                        message={toReceivedMessage(
                                            message.originalReceived,
                                        )}
                                        entityId={entityId}
                                        entityType={entityType}
                                        onCollapse={collapseHandler}
                                        headerActions={
                                            canReply ? (
                                                <ReceivedMessageReplyButtons
                                                    message={message}
                                                    entityId={entityId}
                                                    onReply={handleReply}
                                                    onReplyAll={handleReplyAll}
                                                />
                                            ) : undefined
                                        }
                                    />
                                );
                            }
                        }

                        return (
                            <div
                                key={message.id}
                                className="flex items-center gap-3 px-5 py-2 border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                                onClick={() => toggleMessageExpanded(message.id)}>
                                <ChevronRightIcon
                                    className="h-3 w-3 text-gray-400 flex-shrink-0 transition-transform duration-300"
                                    aria-hidden="true"
                                />
                                <div className="flex-1 min-w-0 flex items-center gap-2">
                                    <span className="font-medium text-sm text-gray-900 truncate max-w-[120px]">
                                        {message.senderName}
                                    </span>
                                    <span className="text-sm text-gray-500 truncate flex-1">
                                        {message.bodyPreview}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
                                    {message.attachmentCount > 0 && (
                                        <MessageIndicatorCount
                                            icon={PaperClipIcon}
                                            label="Attachments"
                                            count={message.attachmentCount}
                                        />
                                    )}
                                    {message.type === "sent" && (
                                        <>
                                            {enhancedMode && (
                                                <>
                                                    <MessageIndicatorCount
                                                        icon={EnvelopeOpenIcon}
                                                        label="Opens"
                                                        count={message.openedCount}
                                                    />
                                                    <MessageIndicatorCount
                                                        icon={CursorArrowRaysIcon}
                                                        label="Clicks"
                                                        count={message.clickCount}
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}
                                    {message.type === "sent" && message.originalSent && (
                                        <SentMessageStatusIndicator
                                            messageType={
                                                message.originalSent
                                                    .messageType as SentMessageType
                                            }
                                            status={message.originalSent.status}
                                            latestEventStatus={
                                                message.originalSent.latestEventStatus
                                            }
                                        />
                                    )}
                                    <span className="min-w-[75px] text-right">
                                        {relativeTimeAgo(message.timestamp)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            {replyState && entityId && entityType && (
                <MessageCompose
                    entityId={entityId}
                    entityType={entityType}
                    initialRecipients={replyState.initialRecipients}
                    initialCc={replyState.initialCc}
                    initialSubject={replyState.initialSubject}
                    initialBody={replyState.initialBody}
                    inReplyToUserMailboxMessageId={
                        replyState.inReplyToUserMailboxMessageId
                    }
                    inReplyToSentMessageId={replyState.inReplyToSentMessageId}
                    replyAction={replyState.replyAction}
                    onDismiss={() => setReplyState(null)}
                />
            )}
        </li>
    );
};
